/**
 * Stem-and-leaf displays, single or back-to-back.
 *
 * Each value is written in whole leaf units — 37 with a leaf unit of 1 is 37 units, 3.7
 * with a leaf unit of 0.1 is also 37 — and split into a stem (everything but the last
 * digit) and a leaf (the last digit). The arithmetic is done on those whole numbers, so
 * 0.29 never becomes 28.999… and lands on the wrong leaf.
 *
 * Conventions, stated because textbooks differ:
 *
 * - Digits finer than the leaf unit are **truncated** (cut off) by default, as Tukey did:
 *   3.78 with a leaf unit of 0.1 is plotted as 3 | 7. Rounding is the alternative.
 * - Negative values get their own stems, and −0 is a stem distinct from 0: −4 is −0 | 4.
 *   Stems run from the most negative at the top to the most positive at the bottom, and
 *   on every stem the leaves increase moving away from the stem, so −2 | 1 3 8 reads
 *   −21, −23, −28.
 * - Split stems show each stem twice: leaves 0–4 on the first line, 5–9 on the second
 *   (on a negative stem the 5–9 line comes first, since those values are smaller).
 * - Stems with no leaves are kept, because a gap in the data is part of its shape.
 */

import { roundTo, stepDecimals } from "@/lib/tool-kit/charts/scale";

export type LeafRounding = "truncate" | "round";

export interface StemLeafOptions {
  /** What one leaf digit is worth: 1 (stems are tens), 0.1 (stems are ones)… */
  leafUnit: number;
  split: boolean;
  rounding: LeafRounding;
}

export interface StemRow {
  /** The stem's digits, without sign. */
  stem: number;
  negative: boolean;
  /** For split stems: `low` holds leaves 0–4 and `high` leaves 5–9. */
  part: "all" | "low" | "high";
  /** One ascending list of leaf digits per dataset. */
  leaves: number[][];
}

export interface StemLeafDisplay {
  leafUnit: number;
  rows: StemRow[];
}

/** The most stems drawn; a leaf unit that would need more is refused. */
export const MAX_STEMS = 120;

/** A value in whole leaf units, with its sign kept apart so −0 survives. */
export interface LeafValue {
  negative: boolean;
  magnitude: number;
}

export function toLeafUnits(
  value: number,
  leafUnit: number,
  rounding: LeafRounding
): LeafValue {
  // Snap away binary noise first: 4.15 / 0.1 is 41.4999… in floating point, which
  // would round down to 41 instead of up to 42, and 0.29 / 0.01 is 28.999…, which
  // would truncate to 28.
  const units = roundTo(Math.abs(value) / leafUnit, 9);
  const magnitude =
    rounding === "round" ? Math.round(units) : Math.floor(units);
  return { negative: value < 0, magnitude };
}

/**
 * A row's position in value order. Positive stem s occupies 2s (low) and 2s + 1 (high);
 * negative stem s occupies −(2s + 1) (low) and −(2s + 2) (high). Unsplit stems use the
 * low slot.
 */
function rowPosition(entry: LeafValue, split: boolean): number {
  const stem = Math.floor(entry.magnitude / 10);
  const high = split && entry.magnitude % 10 >= 5 ? 1 : 0;
  const slot = split ? 2 * stem + high : stem;
  return entry.negative ? -(slot + 1) : slot;
}

function rowAt(position: number, split: boolean, sets: number): StemRow {
  const negative = position < 0;
  const slot = negative ? -position - 1 : position;
  const stem = split ? Math.floor(slot / 2) : slot;
  let part: StemRow["part"] = "all";
  if (split) {
    part = slot % 2 === 1 ? "high" : "low";
  }
  return {
    stem,
    negative,
    part,
    leaves: Array.from({ length: sets }, () => []),
  };
}

/** The number of stems the values need at a leaf unit (split stems count twice). */
export function stemCount(
  values: readonly number[],
  options: StemLeafOptions
): number {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    const position = rowPosition(
      toLeafUnits(value, options.leafUnit, options.rounding),
      options.split
    );
    min = Math.min(min, position);
    max = Math.max(max, position);
  }
  return max >= min ? max - min + 1 : 0;
}

export function buildStemLeaf(
  sets: readonly (readonly number[])[],
  options: StemLeafOptions
): StemLeafDisplay | null {
  const all = sets.flat();
  if (all.length === 0) {
    return null;
  }
  const entries = sets.map((values) =>
    values.map((value) =>
      toLeafUnits(value, options.leafUnit, options.rounding)
    )
  );
  const positions = entries.flat().map((e) => rowPosition(e, options.split));
  const first = Math.min(...positions);
  const last = Math.max(...positions);
  if (last - first + 1 > MAX_STEMS) {
    return null;
  }
  const rows: StemRow[] = [];
  for (let position = first; position <= last; position++) {
    rows.push(rowAt(position, options.split, sets.length));
  }
  for (const [set, values] of entries.entries()) {
    for (const entry of values) {
      const row = rows[rowPosition(entry, options.split) - first];
      row.leaves[set].push(entry.magnitude % 10);
    }
  }
  for (const row of rows) {
    for (const leaves of row.leaves) {
      leaves.sort((a, b) => a - b);
    }
  }
  return { leafUnit: options.leafUnit, rows };
}

/** Decimals a value needs to be written exactly, up to 6. */
function decimalsOf(value: number): number {
  const fraction = Math.abs(value) % 1;
  return fraction === 0 ? 0 : Math.min(6, stepDecimals(fraction));
}

/**
 * The leaf unit "auto" picks: the finest digit the data use, coarsened by tens until
 * the display needs no more than `maxStems` stems. 12, 15, 23 → 1; 3.2, 4.5 → 0.1.
 */
export function autoLeafUnit(
  values: readonly number[],
  { split = false, maxStems = 20 }: { split?: boolean; maxStems?: number } = {}
): number {
  let decimals = 0;
  for (const value of values) {
    decimals = Math.max(decimals, decimalsOf(value));
  }
  let leafUnit = roundTo(10 ** -decimals, decimals);
  for (let pass = 0; pass < 12; pass++) {
    const count = stemCount(values, { leafUnit, split, rounding: "truncate" });
    if (count <= maxStems) {
      break;
    }
    leafUnit = roundTo(leafUnit * 10, Math.max(0, stepDecimals(leafUnit) - 1));
  }
  return leafUnit;
}

/** The value a stem and leaf stand for: −2 | 3 with a leaf unit of 1 is −23. */
export function stemLeafValue(
  row: Pick<StemRow, "stem" | "negative">,
  leaf: number,
  leafUnit: number
): number {
  const magnitude = roundTo(
    (row.stem * 10 + leaf) * leafUnit,
    stepDecimals(leafUnit)
  );
  return row.negative ? -magnitude : magnitude;
}

export interface StemLeafTextOptions {
  /** Column headings: [stem, leaf] or, back-to-back, [left set, stem, right set]. */
  headings: readonly string[];
  /** The sign printed before negative stems. */
  minus?: string;
}

export function stemLabel(row: StemRow, minus = "−"): string {
  return `${row.negative ? minus : ""}${row.stem}`;
}

/**
 * The display as monospace lines — the copyable text version. One dataset gives
 * `stem | leaves`; two give `left leaves | stem | right leaves`, with the left leaves
 * written right to left so they also increase away from the stem.
 */
export function stemLeafLines(
  display: StemLeafDisplay,
  { headings, minus = "−" }: StemLeafTextOptions
): string[] {
  const backToBack = display.rows[0]?.leaves.length === 2;
  const stems = display.rows.map((row) => stemLabel(row, minus));
  const right = display.rows.map((row) => row.leaves.at(-1)?.join(" ") ?? "");
  const stemHeading = backToBack ? headings[1] : headings[0];
  const stemWidth = Math.max(
    [...(stemHeading ?? "")].length,
    ...stems.map((s) => [...s].length)
  );
  if (!backToBack) {
    return [
      `${(stemHeading ?? "").padStart(stemWidth)} | ${headings[1] ?? ""}`,
      ...stems.map((stem, i) => `${stem.padStart(stemWidth)} | ${right[i]}`),
    ];
  }
  const left = display.rows.map((row) =>
    [...row.leaves[0]].reverse().join(" ")
  );
  const leftWidth = Math.max(
    [...(headings[0] ?? "")].length,
    ...left.map((text) => text.length)
  );
  const center = (text: string) => {
    const pad = stemWidth - [...text].length;
    return (
      " ".repeat(Math.ceil(pad / 2)) + text + " ".repeat(Math.floor(pad / 2))
    );
  };
  return [
    `${(headings[0] ?? "").padStart(leftWidth)} | ${center(stemHeading ?? "")} | ${headings[2] ?? ""}`,
    ...stems.map(
      (stem, i) =>
        `${left[i].padStart(leftWidth)} | ${center(stem)} | ${right[i]}`
    ),
  ];
}

/** The first stem and leaf of data set `set`, for the display's key. */
export function firstLeaf(
  display: StemLeafDisplay,
  set: number
): { row: StemRow; leaf: number } | null {
  for (const row of display.rows) {
    const leaves = row.leaves[set] ?? [];
    if (leaves.length > 0) {
      return { row, leaf: leaves[0] };
    }
  }
  return null;
}
